// @flow
import { get } from 'lodash';
import { currentPath, globalT } from '@helpers/helpers';
import type { Action, ContextState, Dispatch } from '../../../types';

// ------------------------------------
// Constants
// ------------------------------------
export const OPEN_TOAST = 'toasts.OPEN_TOAST';
export const CLOSE_ACTIVE_TOAST = 'toasts.CLOSE_ACTIVE_TOAST';

// ------------------------------------
// Actions
// ------------------------------------
export const openToast = (data?: Object = {}) => (
  (dispatch: Dispatch) => {
    dispatch({ type: OPEN_TOAST, data });
  }
);

export const closeActiveToast = () => (
  (dispatch: Dispatch) => {
    dispatch({ type: CLOSE_ACTIVE_TOAST });
  }
);

// A toast its flow closes carries an id, and the flow closes it by that id: the close then leaves
// alone any other toast that came meanwhile (an error, the next report)
export const REPORT_TOAST_ID = 'report';
export const SHARE_TOAST_ID = 'share';

export const closeToastById = (id: string) => ({ type: CLOSE_ACTIVE_TOAST, id });

// AppLayout mounts again with each page and, once its clinics load, closes the toast left from the page before.
// A toast opened along with a change of page says which page it is for (forPage: the patient of another clinic's,
// on the patient list): there it stays, until its own timer or the user closes it. On any other page it goes.
export const closeLeftoverToast = () => ({ type: CLOSE_ACTIVE_TOAST, leftover: true, pathname: currentPath() });

// Shared toasts for the reports xlsx flow (dispatchable action objects)
export const reportPreparingToast = {
  type: OPEN_TOAST,
  data: { type: 'inProgress', text: 'Preparando relatório', id: REPORT_TOAST_ID },
};

export const reportSentByEmailToast = (email: string) => ({
  type: OPEN_TOAST,
  data: {
    type: 'success', text: `O relatório será enviado por email para ${email}`, close: true,
  },
});

export const reportErrorToast = (message?: ?string) => ({
  type: OPEN_TOAST,
  data: {
    type: 'error', text: message || 'Ocorreu um erro ao processar seu relatório.', close: true,
  },
});

// Files left out of an upload for being empty (0 bytes), by name: a file in iCloud, OneDrive or Google Drive that was
// never downloaded to the device, or a failed export or scan. S3 refuses those, so trying again can't help.
// The names go in after translating: i18next would read a {{…}} or $t(…) in a file's name.
const NAMES = '%NAMES%';
export const emptyFilesToast = (names: Array<string>) => ({
  type: OPEN_TOAST,
  data: {
    type: 'warning',
    text: globalT('error/upload/empty_file', 'Arquivo vazio.', { count: names.length, names: NAMES })
      .split(NAMES).join(names.map((name) => `"${name}"`).join(', ')),
    close: true,
  },
});

export const toastActions = {
  openToast,
  closeActiveToast,
};

// ------------------------------------
// Reducer
// ------------------------------------
export default function toastsReducer (state: ContextState, action: Action): ContextState {
  switch (action.type) {
    case OPEN_TOAST:
      return state
        .setIn(['toasts', 'active'], true)
        .setIn(['toasts', 'data'], action.data);

    case CLOSE_ACTIVE_TOAST:
      if (action.id && get(state, ['toasts', 'data', 'id']) !== action.id) return state;
      if (action.leftover && action.pathname && get(state, ['toasts', 'data', 'forPage']) === action.pathname) return state;
      return state
        .setIn(['toasts', 'active'], false)
        .setIn(['toasts', 'data'], null);

    default:
      return state;
  }
}
